import { ValidationRule } from '../types/ValidationRule';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const ValidationRuleService = {
  // Get all validation rules for the current user
  getUserValidationRules: async (): Promise<ValidationRule[]> => {
    try {
      const response = await fetch(`${API_URL}/validation-rules`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch validation rules:', error);
      throw error;
    }
  },

  // Get validation rules filtered by brands
  getUserValidationRulesByBrands: async (brands?: string[]): Promise<ValidationRule[]> => {
    try {
      const url = new URL(`${API_URL}/validation-rules`, window.location.origin);
      if (brands && brands.length > 0) {
        brands.forEach(brand => url.searchParams.append('brands', brand));
      }
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch filtered validation rules:', error);
      throw error;
    }
  },
  
  // Get a single validation rule by ID
  getValidationRuleById: async (id: number): Promise<ValidationRule> => {
    try {
      const response = await fetch(`${API_URL}/validation-rules/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch validation rule with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Create a new validation rule
  createValidationRule: async (rule: ValidationRule): Promise<ValidationRule> => {
    try {
      const response = await fetch(`${API_URL}/validation-rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(rule)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to create validation rule:', error);
      throw error;
    }
  },
  
  // Update an existing validation rule
  updateValidationRule: async (id: number, rule: ValidationRule): Promise<ValidationRule> => {
    try {
      const response = await fetch(`${API_URL}/validation-rules/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(rule)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to update validation rule with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Delete a validation rule
  deleteValidationRule: async (id: number): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/validation-rules/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete validation rule with ID ${id}:`, error);
      throw error;
    }
  }
};

export default ValidationRuleService; 